import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import { Button } from '../ui/button';
import { BankDetails, User } from '../../utils/types';
import { bankAPI, economicCalendarAPI } from '../../services/api';
import { Loader2, Landmark } from 'lucide-react';
import toast from 'react-hot-toast';
import LoaderOverlay from '../common/LoaderOverlay';
import EmptyState from '../common/EmptyState';
import BankFormFields, { CountryOption } from './BankFormFields';
import BankDetailsDisplay from './BankDetailsDisplay';

interface BankDetailsSectionProps {
  user: User | null;
  onSubmit?: (data: BankDetails, bankFile: File | null) => Promise<void>;
  pageLoading: boolean;
  setPageLoading: (loading: boolean) => void;
  isAddMode?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  onNavigateToKyc?: () => void;
  hideSubmitButtons?: boolean;
  onFormDataChange?: (data: {
    accountName: string;
    accountNumber: string;
    bankName: string;
    branchName?: string;
    ifscCode?: string;
    bankCountry?: string;
    currency?: string;
    routingNumber?: string;
    sortCode?: string;
    bsbNumber?: string;
    iban?: string;
    bicSwift?: string;
    institutionNumber?: string;
    transitNumber?: string;
    verificationDocumentType?: string;
    verificationDocument: File | null;
  }) => void;
}

const BankDetailsSection = ({
  user,
  onSubmit,
  pageLoading,
  setPageLoading,
  isAddMode = false,
  isOpen = false,
  onClose,
  onSuccess,
  onNavigateToKyc,
  hideSubmitButtons = false,
  onFormDataChange,
}: BankDetailsSectionProps) => {
  const [isEditingBank, setIsEditingBank] = useState(isAddMode);
  const [bankFile, setBankFile] = useState<File | null>(null);
  const [blobPreview, setBlobPreview] = useState<string | null>(null);
  const [existingDocCleared, setExistingDocCleared] = useState(false);
  const [countriesList, setCountriesList] = useState<CountryOption[]>([]);
  const [isCountriesLoading, setIsCountriesLoading] = useState(true);

  const {
    register: registerBank,
    handleSubmit: handleSubmitBank,
    reset: resetBank,
    setValue: setBankValue,
    watch: watchBank,
    formState: { errors: bankErrors, isDirty: isBankDirty },
  } = useForm<BankDetails>({
    mode: 'onChange',
    defaultValues: user?.bankDetails || {
      bankCountry: '',
      currency: '',
      accountName: '',
      accountNumber: '',
      bankName: '',
      ifscCode: '',
      branchName: '',
      routingNumber: '',
      sortCode: '',
      bsbNumber: '',
      iban: '',
      bicSwift: '',
      institutionNumber: '',
      transitNumber: '',
      verificationDocumentType: '',
    },
  });

  const bankFormValues = watchBank();
  const selectedCountry = bankFormValues.bankCountry || '';
  const selectedDocType = bankFormValues.verificationDocumentType || '';

  const bankFilePreview: string | null =
    blobPreview ??
    (!existingDocCleared
      ? (user?.bankDetails?.verificationDocument ?? null)
      : null);

  const resolveCountryIso = (countryName: string): string => {
    if (!countryName) return '';
    const matched = countriesList.find(
      c => c.name.toLowerCase() === countryName.trim().toLowerCase()
    );
    return (matched?.isoCode || '').toUpperCase();
  };

  useEffect(() => {
    let isMounted = true;
    const fetchCountries = async () => {
      setIsCountriesLoading(true);
      try {
        const response = await economicCalendarAPI.getCountries();
        if (response && response.success && Array.isArray(response.data)) {
          const mapped: CountryOption[] = response.data
            .map(
              (c: {
                name?: string;
                currency?: string;
                isoCode?: string;
                banking?: any;
              }) => ({
                name: c.name ?? '',
                currency: c.currency || 'USD',
                isoCode: c.isoCode,
                banking: c.banking,
              })
            )
            .filter((c: CountryOption) => Boolean(c.name && c.currency));
          if (isMounted) setCountriesList(mapped);
        }
      } catch (err) {
        console.warn('Could not load countries from API', err);
      } finally {
        if (isMounted) setIsCountriesLoading(false);
      }
    };
    fetchCountries();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (user?.bankDetails) {
      resetBank({
        bankCountry: user.bankDetails.bankCountry || '',
        currency: user.bankDetails.currency || '',
        institutionNumber: user.bankDetails.institutionNumber || '',
        transitNumber: user.bankDetails.transitNumber || '',
        accountName: user.bankDetails.accountName || '',
        accountNumber: user.bankDetails.accountNumber || '',
        bankName: user.bankDetails.bankName || '',
        branchName: user.bankDetails.branchName || '',
        ifscCode: user.bankDetails.ifscCode || '',
        routingNumber: user.bankDetails.routingNumber || '',
        sortCode: user.bankDetails.sortCode || '',
        bsbNumber: user.bankDetails.bsbNumber || '',
        iban: user.bankDetails.iban || '',
        bicSwift: user.bankDetails.bicSwift || '',
        verificationDocumentType:
          user.bankDetails.verificationDocumentType || 'CANCELLED_CHEQUE',
        verificationDocument: user.bankDetails.verificationDocument || '',
      });
    }
  }, [user?.bankDetails, resetBank]);

  useEffect(() => {
    return () => {
      if (blobPreview) URL.revokeObjectURL(blobPreview);
    };
  }, [blobPreview]);

  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange({
        accountName: bankFormValues.accountName || '',
        accountNumber: bankFormValues.accountNumber || '',
        institutionNumber: bankFormValues.institutionNumber || '',
        transitNumber: bankFormValues.transitNumber || '',
        bankName: bankFormValues.bankName || '',
        branchName: bankFormValues.branchName || '',
        ifscCode: bankFormValues.ifscCode || '',
        bankCountry: bankFormValues.bankCountry || '',
        currency: bankFormValues.currency || '',
        routingNumber: bankFormValues.routingNumber || '',
        sortCode: bankFormValues.sortCode || '',
        bsbNumber: bankFormValues.bsbNumber || '',
        iban: bankFormValues.iban || '',
        bicSwift: bankFormValues.bicSwift || '',
        verificationDocumentType: bankFormValues.verificationDocumentType || '',
        verificationDocument: bankFile,
      });
    }
  }, [bankFormValues, bankFile, onFormDataChange]);

  const handleCountryChange = (value: string) => {
    const matched =
      countriesList.find(c => c.name === value) ||
      countriesList.find(c => c.name.toLowerCase() === value.toLowerCase());

    const finalCountryName = matched?.name || value;
    setBankValue('bankCountry', finalCountryName, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (matched?.currency) {
      setBankValue('currency', matched.currency, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    setBankValue('accountName', '', { shouldValidate: false });
    setBankValue('bankName', '', { shouldValidate: false });
    setBankValue('verificationDocumentType', '', { shouldValidate: false });
    setBankFile(null);
    setBlobPreview(null);
    setExistingDocCleared(true);

    const allKnownKeys: string[] = [
      'accountNumber',
      'ifscCode',
      'branchName',
      'routingNumber',
      'sortCode',
      'bsbNumber',
      'institutionNumber',
      'transitNumber',
      'iban',
      'bicSwift',
    ];

    allKnownKeys.forEach(k => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setBankValue(k as any, '', { shouldValidate: false });
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setBankValue('verificationDocument', undefined as any);
  };

  const handleBankFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'application/pdf',
    ];
    const isPdfExt = file.name.toLowerCase().endsWith('.pdf');
    if (file.type && !allowedTypes.includes(file.type) && !isPdfExt) {
      toast.error(
        'Invalid file type. Allowed formats: PNG, JPG, JPEG, WEBP, PDF'
      );
      return;
    }
    if (blobPreview) URL.revokeObjectURL(blobPreview);
    setBlobPreview(URL.createObjectURL(file));
    setExistingDocCleared(false);
    setBankFile(file);
    setBankValue('verificationDocument', file as any, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleClearFile = () => {
    setBankFile(null);
    setBlobPreview(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setBankValue('verificationDocument', undefined as any);
  };

  const handleDocTypeChange = (value: string) => {
    setBankValue('verificationDocumentType', value, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (value !== selectedDocType) {
      handleClearFile();
    }
  };

  const handleCancel = () => {
    setIsEditingBank(false);
    resetBank(user?.bankDetails || undefined);
    setBankFile(null);
    setBlobPreview(null);
    setExistingDocCleared(false);
  };

  const handleEditClick = () => {
    if (!user?.bankDetails) {
      toast.error('Please verify the bank details');
      return;
    }
    setIsEditingBank(true);
  };

  const handleFormSubmit = async (data: BankDetails) => {
    setPageLoading(true);
    try {
      if (
        selectedDocType &&
        !bankFile &&
        !user?.bankDetails?.verificationDocument
      ) {
        toast.error(
          'Verification document upload is required when a document type is selected.'
        );
        setPageLoading(false);
        return;
      }

      if (isAddMode) {
        if (!bankFile) {
          toast.error('Please upload bank verification document');
          setPageLoading(false);
          return;
        }

        const matched =
          countriesList.find(c => c.name === data.bankCountry) ||
          countriesList.find(
            c => c.name.toLowerCase() === (data.bankCountry || '').toLowerCase()
          );
        const activeFields = matched?.banking?.fields || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dynamicPayload: Record<string, any> = {};

        if (activeFields.length > 0) {
          activeFields.forEach(f => {
            const val = data[f.key as keyof BankDetails];
            if (val !== undefined && val !== null && val !== '') {
              const vType = (f.validation || '').toUpperCase();
              if (typeof val === 'string') {
                if (
                  vType === 'IFSC' ||
                  vType === 'IBAN' ||
                  vType === 'BIC_SWIFT' ||
                  vType === 'SWIFT' ||
                  vType === 'BIC'
                ) {
                  dynamicPayload[f.key] = val.toUpperCase().trim();
                } else {
                  dynamicPayload[f.key] = val.trim();
                }
              } else {
                dynamicPayload[f.key] = val;
              }
            }
          });
        } else {
          if (data.accountNumber)
            dynamicPayload.accountNumber = data.accountNumber;
          if (data.ifscCode)
            dynamicPayload.ifscCode = data.ifscCode.toUpperCase();
          if (data.branchName) dynamicPayload.branchName = data.branchName;
          if (data.routingNumber)
            dynamicPayload.routingNumber = data.routingNumber;
          if (data.sortCode) dynamicPayload.sortCode = data.sortCode;
          if (data.bsbNumber) dynamicPayload.bsbNumber = data.bsbNumber;
          if (data.institutionNumber)
            dynamicPayload.institutionNumber = data.institutionNumber;
          if (data.transitNumber)
            dynamicPayload.transitNumber = data.transitNumber;
          if (data.iban) dynamicPayload.iban = data.iban.toUpperCase();
          if (data.bicSwift)
            dynamicPayload.bicSwift = data.bicSwift.toUpperCase();
        }

        const payload = {
          accountName: data.accountName,
          bankName: data.bankName,
          bankCountry: data.bankCountry,
          currency: data.currency,
          ...dynamicPayload,
          verificationDocumentType: selectedDocType,
          verificationDocument: bankFile,
        };

        const response = await bankAPI.addBankDetails(payload);
        if (response.success) {
          const successMsg =
            response.message || 'Bank details submitted successfully';
          toast.success(successMsg, { id: 'api-success' });
          onSuccess?.();
          onClose?.();
          setIsEditingBank(false);
          setBankFile(null);
          setBlobPreview(null);
        } else {
          const errMsg =
            response.error ||
            response.message ||
            'Failed to submit bank details';
          toast.error(errMsg, { id: 'api-error' });
        }
      } else {
        if (onSubmit) {
          await onSubmit(data, bankFile);
          setIsEditingBank(false);
          setBankFile(null);
          setBlobPreview(null);
        }
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message?: string;
      };
      const errMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to submit bank details';
      toast.error(errMsg);
    } finally {
      setPageLoading(false);
    }
  };

  const renderBankForm = () => (
    <form onSubmit={handleSubmitBank(handleFormSubmit)} className="space-y-5">
      <BankFormFields
        register={registerBank}
        setValue={setBankValue}
        errors={bankErrors}
        selectedCountry={selectedCountry}
        countryIsoCode={resolveCountryIso(selectedCountry)}
        selectedDocType={selectedDocType}
        countriesList={countriesList}
        isCountriesLoading={isCountriesLoading}
        bankFile={bankFile}
        bankFilePreview={bankFilePreview}
        onCountryChange={handleCountryChange}
        onDocTypeChange={handleDocTypeChange}
        onFileUpload={handleBankFileUpload}
        onClearFile={handleClearFile}
      />

      {!hideSubmitButtons && (
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-3 border-t border-border">
          <Button
            variant="outline"
            size="default"
            className="w-full sm:w-auto h-10 px-6 text-sm font-semibold bg-red-500 text-white border-red-500 hover:bg-red-600 hover:text-white"
            onClick={handleCancel}
            type="button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={pageLoading || (!isBankDirty && !isAddMode && !bankFile)}
            className="w-full sm:w-auto h-10 px-6 text-sm font-semibold"
          >
            {pageLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {isAddMode ? 'Verify' : 'Save Details'}
          </Button>
        </div>
      )}
    </form>
  );

  if (isAddMode) {
    if (!isOpen) return null;
    return (
      <LoaderOverlay
        isLoading={pageLoading}
        loadingText="Verifying bank details..."
      >
        {renderBankForm()}
      </LoaderOverlay>
    );
  }

  const bankData = user?.bankDetails;

  return (
    <Card className="overflow-hidden border-primary/10 shadow-md">
      <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-lg flex items-center gap-2">
              <Landmark className="h-5 w-5 text-primary" />
              Bank Details
            </CardTitle>
            <CardDescription>
              Primary account for your transactions
            </CardDescription>
          </div>

          <div className="flex items-center gap-2">
            {!isEditingBank && (
              <Button
                size="sm"
                className="w-full sm:w-auto h-10 px-6 text-sm font-semibold"
                onClick={!bankData ? onNavigateToKyc : handleEditClick}
              >
                {!bankData ? 'Click here' : 'Edit'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        {!isEditingBank ? (
          !bankData ? (
            <EmptyState
              icon={Landmark}
              title="No Bank Details Added"
              description="Please complete your bank verification to enable withdrawals and deposits."
            />
          ) : (
            <BankDetailsDisplay bankData={bankData} />
          )
        ) : (
          renderBankForm()
        )}
      </CardContent>
    </Card>
  );
};

export default BankDetailsSection;
